import React from "react";
import * as MUI from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import TextEditorInput from "../../../../components/form/TextEditorInput";
import ConfirmAndCancelButtons from "../../../../components/buttons/ConfirmAndCancelButtons";
import TemplateCategoryMultiSelect from "./TemplateCategoryMultiSelect";
import useSubmit from "../../../master/species/hooks/useSubmit";
import usePut from "../../../../hooks/usePut";
import * as apiPaths from "../../../../enums/ApiPaths";
import * as keys from "../../../../enums/QueryKeys";

export default function TemplateFormDialog({ open, onClose, clinicId, initialValues }) {
  const { t } = useTranslation();
  const theme = MUI.useTheme();
  const fullScreen = MUI.useMediaQuery(theme.breakpoints.down("md"));
  const isEditMode = Boolean(initialValues);

  const {
    formState: { errors },
    register,
    handleSubmit,
    control,
  } = useForm({
    defaultValues: {
      title: initialValues?.title ?? "",
      category: initialValues?.category ?? [],
      description: initialValues?.description ?? "",
    },
  });

  const templateUrl = `${apiPaths.TEMPLATE}${clinicId}`;
  const create = useSubmit(null, templateUrl, null, [keys.TEMPLATES_LIST + clinicId]);
  const edit = usePut(
    templateUrl,
    [keys.TEMPLATES_LIST + clinicId],
    [keys.TEMPLATES_LIST + clinicId],
  );

  const submitData = async (data) => {
    const obj = isEditMode ? { ...initialValues, ...data } : data;
    const success = isEditMode
      ? await edit.submitCallback(obj)
      : await create.submitCallback(obj);
    if (success) {
      onClose();
    }
  };

  return (
    <MUI.Dialog
      open={open}
      onClose={onClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}
    >
      <MUI.Stack direction="row" justifyContent="flex-end" sx={{ p: 1 }}>
        <MUI.IconButton
          onClick={onClose}
          size="small"
          data-testid="template.form-dialog.close-trigger"
        >
          <CloseIcon fontSize="small" />
        </MUI.IconButton>
      </MUI.Stack>
      <MUI.DialogContent
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          pt: 0,
          bgcolor: (theme) =>
            theme.palette.mode === "light" ? "grey.50" : "rgba(255,255,255,0.02)",
        }}
      >
        <form onSubmit={handleSubmit(submitData)}>
          <MUI.Stack spacing={4}>
            <MUI.Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
                alignItems: "flex-start",
              }}
            >
              <MUI.Box sx={{ flex: 2 }}>
                <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                  {t("Title")} *
                </MUI.Typography>
                <MUI.TextField
                  fullWidth
                  variant="outlined"
                  size="small"
                  placeholder={t("DoctorsLetterTitlePlaceholder")}
                  inputProps={{
                    maxLength: 120,
                    "data-testid": "template.form-dialog.title-input",
                  }}
                  {...register("title", {
                    required: t("EmptyField"),
                    maxLength: { value: 120 },
                  })}
                  error={!!errors.title}
                  helperText={errors.title?.message || `${t("MaxCharacters")}: 120`}
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1 } }}
                />
              </MUI.Box>

              <MUI.Box sx={{ flex: 1, width: "100%" }}>
                <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                  {t("Categories")}
                </MUI.Typography>
                <Controller
                  control={control}
                  name="category"
                  defaultValue={initialValues?.category ?? []}
                  render={({ field }) => (
                    <TemplateCategoryMultiSelect
                      value={field.value}
                      onChange={field.onChange}
                      size="small"
                    />
                  )}
                />
              </MUI.Box>
            </MUI.Box>

            <MUI.Box>
              <MUI.Typography variant="subtitle1" gutterBottom sx={{ mb: 1.5, fontWeight: 500 }}>
                {t("Description")}
              </MUI.Typography>
              <Controller
                control={control}
                name="description"
                render={({ field }) => (
                  <TextEditorInput
                    onChange={field.onChange}
                    value={field.value}
                    testId="template.form-dialog.description-input"
                  />
                )}
              />
            </MUI.Box>

            <MUI.Divider />
            <MUI.Typography variant="caption" color="error">
              {`* ${t("RequiredFields")}`}
            </MUI.Typography>

            <ConfirmAndCancelButtons
              useCustomLoadingButtonOnConfirm={true}
              isConfirmLoading={isEditMode ? edit.loading : create.loading}
              confirmTitle="Save"
              cancelTitle=""
              confirmButtonType="submit"
              testId="template.form-dialog"
            />
          </MUI.Stack>
        </form>
      </MUI.DialogContent>
    </MUI.Dialog>
  );
}